// src/services/MemoryGraphService.ts
// Query layer across all memory tables.
// Answers: "Show me everything that happened to this person, in order."

import { supabase } from '@/lib/supabase';
import { MemoryNode, MemoryEdge } from '@/types/Journey.types';
import { EventSystem } from './EventSystem';
import { JourneyStateService } from './JourneyStateService';

export class MemoryGraphService {
  private userId: string;
  private eventSystem: EventSystem;
  private journeyState: JourneyStateService;

  constructor(userId: string) {
    this.userId = userId;
    this.eventSystem = new EventSystem(userId);
    this.journeyState = new JourneyStateService(userId);
  }

  async getCompleteTimeline(): Promise<MemoryNode[]> {
    const [events, state] = await Promise.all([
      this.eventSystem.getTimeline({ limit: 100 }),
      this.journeyState.getJourneyState(),
    ]);

    const nodes: MemoryNode[] = events.map(e => ({
      id: e.id,
      type: 'event' as const,
      data: e,
      timestamp: e.createdAt,
    }));

    if (state) {
      nodes.push({
        id: state.id,
        type: 'assessment' as const,
        data: state,
        timestamp: state.createdAt,
      });
    }

    return nodes.sort(
      (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );
  }

  async getRelationshipGraph(): Promise<{ nodes: MemoryNode[]; edges: MemoryEdge[] }> {
    const [events, state] = await Promise.all([
      this.eventSystem.getTimeline({ limit: 50 }),
      this.journeyState.getJourneyState(),
    ]);

    const nodes: MemoryNode[] = [];
    const edges: MemoryEdge[] = [];

    // User root node
    nodes.push({
      id: this.userId,
      type: 'user' as const,
      data: { id: this.userId },
      timestamp: new Date().toISOString(),
    });

    // Journey state node
    if (state) {
      nodes.push({
        id: state.id,
        type: 'assessment' as const,
        data: state,
        timestamp: state.createdAt,
      });
      edges.push({ from: this.userId, to: state.id, type: 'created' });
    }

    // Event nodes
    events.forEach(e => {
      nodes.push({
        id: e.id,
        type: 'event' as const,
        data: e,
        timestamp: e.createdAt,
      });
      edges.push({ from: this.userId, to: e.id, type: 'created' });
    });

    // Chain events chronologically
    for (let i = 0; i < events.length - 1; i++) {
      edges.push({ from: events[i].id, to: events[i + 1].id, type: 'triggered' });
    }

    return { nodes, edges };
  }

  async getProfileAtTime(timestamp: string): Promise<any> {
    const events = await this.eventSystem.getTimeline({ endDate: timestamp, limit: 1 });
    return events[0]?.payload ?? null;
  }

  async getBlockingTasks(applicationId: string): Promise<any[]> {
    try {
      const { data, error } = await supabase
        .from('dependencies')
        .select('depends_on, tasks!dependencies_task_id_fkey(status)')
        .eq('task_id', applicationId);

      if (error || !data) return [];

      return data.filter((d: any) => d.tasks?.status !== 'completed');
    } catch {
      return [];
    }
  }

  async getSimulationHistory(): Promise<any[]> {
    try {
      const { data, error } = await supabase
        .from('compass_simulations')
        .select('*')
        .eq('user_id', this.userId)
        .order('created_at', { ascending: false });

      if (error || !data) return [];
      return data;
    } catch {
      return [];
    }
  }
}
